import React, { useState } from 'react';
import { useForm } from '../hooks/useForm';
import { sendToDiscordWebhook } from '../utils/api';

const ContactForm: React.FC = () => {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const initialValues = {
    name: '',
    email: '',
    subject: '',
    message: ''
  };

  const validateForm = (values: Record<string, string>) => {
    const errors: Record<string, string> = {};
    
    if (!values.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!values.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = 'Invalid email address';
    }
    
    if (!values.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    
    if (!values.message.trim()) {
      errors.message = 'Message is required';
    }
    
    return errors;
  };

  const { values, errors, isSubmitting, handleChange, handleSubmit, resetForm } = useForm(
    initialValues,
    validateForm
  );

  const onSubmit = async (formData: Record<string, string>) => {
    try {
      const webhookUrl = 'https://discord.com/api/webhooks/1374350096551907370/kX7eVyhXblPd0hD2x-baMYyI3uAE9P4sTDt6q48c1KgM3JyLFN74ZDsctHvGJPUjzjGL';
      
      setSubmitStatus('idle');
      setStatusMessage('');
      
      const response = await sendToDiscordWebhook(webhookUrl, formData);
      
      if (response.ok) {
        setSubmitStatus('success');
        setStatusMessage('Your message has been sent successfully!');
        resetForm();
      } else {
        setSubmitStatus('error');
        setStatusMessage('Failed to send message. Please try again later.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setStatusMessage('An error occurred. Please try again later.');
      console.error('Error sending form:', error);
    }
  };

  return (
    <div className="w-full">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-[#2b603e] mb-4 pb-2 border-b-4 border-[#2b603e] inline-block">Contact Us</h2>
        <p className="text-gray-600">We'd love to hear from you. Send us a message.</p>
      </div>
      
      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg text-center">
          {statusMessage}
        </div>
      )}
      
      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg text-center">
          {statusMessage}
        </div>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-[#2b603e] font-semibold mb-2">
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={values.name}
            onChange={handleChange}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 ${
              errors.name 
                ? 'border-red-500 focus:ring-red-200' 
                : 'border-[#2b603e]/30 focus:border-[#2b603e] focus:ring-[#2b603e]/20'
            }`}
            placeholder="Your full name"
          />
          {errors.name && <p className="mt-2 text-red-500 text-sm">{errors.name}</p>}
        </div>
        
        <div>
          <label htmlFor="email" className="block text-[#2b603e] font-semibold mb-2">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={values.email}
            onChange={handleChange}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 ${
              errors.email 
                ? 'border-red-500 focus:ring-red-200' 
                : 'border-[#2b603e]/30 focus:border-[#2b603e] focus:ring-[#2b603e]/20'
            }`}
            placeholder="your.email@example.com"
          />
          {errors.email && <p className="mt-2 text-red-500 text-sm">{errors.email}</p>}
        </div>
        
        <div>
          <label htmlFor="subject" className="block text-[#2b603e] font-semibold mb-2">
            Subject
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={values.subject}
            onChange={handleChange}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 ${
              errors.subject 
                ? 'border-red-500 focus:ring-red-200' 
                : 'border-[#2b603e]/30 focus:border-[#2b603e] focus:ring-[#2b603e]/20'
            }`}
            placeholder="Purpose of your message"
          />
          {errors.subject && <p className="mt-2 text-red-500 text-sm">{errors.subject}</p>}
        </div>
        
        <div>
          <label htmlFor="message" className="block text-[#2b603e] font-semibold mb-2">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            value={values.message}
            onChange={handleChange}
            rows={5}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 resize-none ${
              errors.message 
                ? 'border-red-500 focus:ring-red-200' 
                : 'border-[#2b603e]/30 focus:border-[#2b603e] focus:ring-[#2b603e]/20'
            }`}
            placeholder="Write your message here..."
          ></textarea>
          {errors.message && <p className="mt-2 text-red-500 text-sm">{errors.message}</p>}
        </div>
        
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-4 px-6 rounded-lg text-white font-bold uppercase tracking-wider transition-all duration-300 transform hover:scale-105 ${
            isSubmitting 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-[#2b603e] hover:bg-[#1a3d25] shadow-lg hover:shadow-xl'
          }`}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;